/**
 * PRP-INTAKE-2: one group of requirements on the Intake tab (Claim, an elevation, a room): each
 * requirement's question, value, source badge, and the control to answer or change it per
 * `input.type`, plus the draft helpers the tab uses to batch a stage's answers. Split out of
 * IntakeTab.tsx to keep both under the god-file cap.
 */
import { useEffect, useId, useState } from 'react';

import type { IntakeAnswer, IntakeGroup, IntakeRequirement, IntakeSource } from '../../lib/intakeApi';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Pill } from './ui/pill';
import type { PillKind } from './ui/pill';
import { Select } from './ui/select';

const SOURCE: Record<IntakeSource, { label: string; kind: PillKind }> = {
  document: { label: 'Document', kind: 'info' },
  photo: { label: 'Photo', kind: 'info' },
  floor_plan: { label: 'Plan', kind: 'info' },
  notes: { label: 'Notes', kind: 'info' },
  imported_estimate: { label: 'Imported estimate', kind: 'info' },
  inference: { label: 'Inference', kind: 'neutral' },
  answered: { label: 'Answered', kind: 'ok' },
  default: { label: 'Default', kind: 'neutral' },
};

/** Choices above this count render as a select instead of radios. */
const RADIO_MAX = 6;

/** What the user has picked or typed for one requirement, before it is saved. */
export type Draft = { choice: number | null; choices: number[]; text: string };

/** The choice number a stored value stands for (a number, or a label). */
function choiceOf(r: IntakeRequirement, v: unknown): number | null {
  const cs = r.input.choices ?? [];
  const hit = cs.find((c) => c.choice === v || c.label === v || (v == null && c.label === r.value_label));
  return hit ? hit.choice : null;
}

function isBoolChoice(r: IntakeRequirement): boolean {
  return r.input.type === 'bool' && !(r.input.choices ?? []).length;
}

export function initialDraft(r: IntakeRequirement): Draft {
  const v = r.value;
  if (r.input.type === 'multi') {
    const list = Array.isArray(v) ? v : [];
    return { choice: null, choices: list.map((x) => choiceOf(r, x)).filter((x): x is number => x != null), text: '' };
  }
  if (isBoolChoice(r)) {
    const yes = v === true || v === 'yes' || v === 1;
    const no = v === false || v === 'no' || v === 0;
    return { choice: null, choices: [], text: yes ? 'yes' : no ? 'no' : '' };
  }
  if (r.input.type === 'choice' || r.input.type === 'bool') {
    return { choice: v == null && !r.value_label ? null : choiceOf(r, v), choices: [], text: '' };
  }
  return { choice: null, choices: [], text: v == null ? '' : String(v) };
}

/** The answer a draft sends, or null when it is incomplete. */
export function answerOf(r: IntakeRequirement, d: Draft): IntakeAnswer | null {
  if (r.input.type === 'multi') return { id: r.id, choices: [...d.choices].sort((a, b) => a - b) };
  if (r.input.type === 'choice' || (r.input.type === 'bool' && !isBoolChoice(r)))
    return d.choice == null ? null : { id: r.id, choice: d.choice };
  const t = d.text.trim();
  if (!t) return null;
  if (r.input.type === 'number') {
    const n = Number(t);
    return Number.isFinite(n) ? { id: r.id, value: n } : null;
  }
  return { id: r.id, value: t };
}

export function sameDraft(a: Draft, b: Draft): boolean {
  return a.choice === b.choice && a.text.trim() === b.text.trim() &&
    [...a.choices].sort().join(',') === [...b.choices].sort().join(',');
}

function shownValue(r: IntakeRequirement): string | null {
  if (r.value_label) return r.value_label;
  const v = r.value;
  if (v == null || v === '') return null;
  if (Array.isArray(v)) return v.map(String).join(', ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return r.input.unit ? `${v} ${r.input.unit}` : String(v);
}

function SourceBadge({ r }: { r: IntakeRequirement }) {
  const meta = r.source ? SOURCE[r.source] : null;
  if (!meta || r.status === 'open') {
    return (
      <Pill kind="warn" className="h-[18px] px-1.5">
        Open
      </Pill>
    );
  }
  return (
    <Pill kind={meta.kind} className="h-[18px] px-1.5">
      {meta.label}
    </Pill>
  );
}

export interface GroupBlockProps {
  g: IntakeGroup;
  drafts: Record<string, Draft>;
  /** Reasons the server rejected an answer, by requirement id. */
  errors: Record<string, string>;
  onDraft: (_r: IntakeRequirement, _d: Draft) => void;
  disabled: boolean;
}

export function IntakeGroupBlock({ g, drafts, errors, onDraft, disabled }: GroupBlockProps) {
  return (
    <div className="mt-3" data-testid={`intake-group-${g.group}`}>
      <h4 className="mb-1 text-[12px] font-[600] text-[var(--tx-lo)]">
        {g.group}
        {g.kind === 'elevation' && <span className="ml-1 font-[450] text-[var(--tx-faint)]">elevation</span>}
      </h4>
      <ul className="m-0 list-none space-y-3 p-0">
        {g.requirements.map((r) => (
          <RequirementRow key={r.id} r={r} draft={drafts[r.id]} error={errors[r.id]} onDraft={onDraft} disabled={disabled} />
        ))}
      </ul>
    </div>
  );
}

interface RequirementRowProps {
  r: IntakeRequirement;
  draft?: Draft;
  error?: string;
  onDraft: (_r: IntakeRequirement, _d: Draft) => void;
  disabled: boolean;
}

function RequirementRow({ r, draft, error, onDraft, disabled }: RequirementRowProps) {
  const [open, setOpen] = useState(false);
  // A refetch (after a save) hands a new requirement object: close the editor.
  useEffect(() => setOpen(false), [r]);
  const isOpen = r.status === 'open';
  const editing = isOpen || open || draft != null;
  const shown = shownValue(r);
  const d = draft ?? initialDraft(r);

  return (
    <li className="min-w-0 break-words text-[13px] text-[var(--tx-hi)]" data-testid={`intake-req-${r.id}`}>
      <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] text-[var(--tx-mid)]">{r.question}</span>
          <span className="font-[550]">{shown ?? 'Not answered'}</span>
          {r.reason && <span className="block text-[11.5px] text-[var(--tx-lo)]">{r.reason}</span>}
        </span>
        <SourceBadge r={r} />
        {!editing && (
          <Button size="sm" disabled={disabled} onClick={() => setOpen(true)} aria-label={`Change: ${r.question}`}>
            Change
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-1 text-[12px] text-[var(--warn)]" data-testid={`intake-req-error-${r.id}`}>
          Not saved: {error}
        </p>
      )}
      {editing && (
        <div className="mt-1.5 border-l border-[var(--bd-subtle)] pl-3">
          <Control r={r} d={d} onDraft={(nd) => onDraft(r, nd)} disabled={disabled} />
          {!isOpen && (
            <Button
              size="sm"
              variant="ghost"
              className="mt-1"
              disabled={disabled}
              onClick={() => {
                onDraft(r, initialDraft(r));
                setOpen(false);
              }}
              aria-label={`Cancel: ${r.question}`}
            >
              Cancel
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

interface ControlProps {
  r: IntakeRequirement;
  d: Draft;
  onDraft: (_d: Draft) => void;
  disabled: boolean;
}

const OPTION = 'flex min-h-[28px] cursor-pointer items-start gap-2 text-[12.5px]';
const MARK = 'mt-[3px] accent-[var(--accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent-line)]';

function choiceLabel(r: IntakeRequirement, c: { choice: number; label: string }): string {
  return r.recommended === c.choice ? `${c.label} (recommended)` : c.label;
}

function Control({ r, d, onDraft, disabled }: ControlProps) {
  const uid = useId();
  const choices = r.input.choices ?? [];
  const t = r.input.type;

  if (t === 'multi') {
    return (
      <fieldset className="m-0 min-w-0 border-0 p-0" disabled={disabled}>
        <legend className="sr-only">{r.question}</legend>
        {choices.map((c) => (
          <label key={c.choice} className={OPTION}>
            <input
              type="checkbox"
              className={MARK}
              checked={d.choices.includes(c.choice)}
              onChange={(e) =>
                onDraft({
                  ...d,
                  choices: e.target.checked ? [...d.choices, c.choice] : d.choices.filter((x) => x !== c.choice),
                })
              }
            />
            <span>{choiceLabel(r, c)}</span>
          </label>
        ))}
      </fieldset>
    );
  }

  if (t === 'bool' && choices.length === 0) {
    return (
      <fieldset className="m-0 flex min-w-0 flex-wrap gap-x-4 border-0 p-0" disabled={disabled}>
        <legend className="sr-only">{r.question}</legend>
        {(['yes', 'no'] as const).map((v) => (
          <label key={v} className={OPTION}>
            <input
              type="radio"
              name={uid}
              className={MARK}
              checked={d.text === v}
              onChange={() => onDraft({ ...d, text: v })}
            />
            <span>{v === 'yes' ? 'Yes' : 'No'}</span>
          </label>
        ))}
      </fieldset>
    );
  }

  if (t === 'choice' || t === 'bool') {
    if (choices.length > RADIO_MAX) {
      return (
        <Select
          aria-label={r.question}
          disabled={disabled}
          value={d.choice == null ? '' : String(d.choice)}
          onChange={(e) => onDraft({ ...d, choice: e.target.value ? Number(e.target.value) : null })}
          className="max-w-full"
        >
          <option value="">Choose…</option>
          {choices.map((c) => (
            <option key={c.choice} value={c.choice}>
              {choiceLabel(r, c)}
            </option>
          ))}
        </Select>
      );
    }
    return (
      <fieldset className="m-0 min-w-0 border-0 p-0" disabled={disabled}>
        <legend className="sr-only">{r.question}</legend>
        {choices.map((c) => (
          <label key={c.choice} className={OPTION}>
            <input
              type="radio"
              name={uid}
              className={MARK}
              checked={d.choice === c.choice}
              onChange={() => onDraft({ ...d, choice: c.choice })}
            />
            <span>{choiceLabel(r, c)}</span>
          </label>
        ))}
      </fieldset>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      <Input
        id={uid}
        aria-label={r.question}
        disabled={disabled}
        type={t === 'number' ? 'number' : 'text'}
        inputMode={t === 'number' ? 'decimal' : undefined}
        maxLength={t === 'text' ? 300 : undefined}
        value={d.text}
        onChange={(e) => onDraft({ ...d, text: e.target.value })}
        className={t === 'number' ? 'w-32' : 'min-w-0 flex-1'}
      />
      {t === 'number' && r.input.unit && <span className="text-[12px] text-[var(--tx-lo)]">{r.input.unit}</span>}
    </div>
  );
}
